import type {
  CompiledKMachine,
  CompiledKRule,
  KCanonicalComplex,
  KComplex,
  KEdge,
  KExecutionState,
  KMachine,
  KPartition,
  KRule,
  KRunResult,
  KSignature,
  KStepTrace,
  KTypeId,
  KVertex,
  KVertexId,
} from './types'

export class KValidationError extends Error {
  readonly code: string
  readonly context: Readonly<Record<string, unknown>>

  constructor(
    code: string,
    message: string,
    context: Readonly<Record<string, unknown>> = {},
  ) {
    super(message)
    this.name = 'KValidationError'
    this.code = code
    this.context = context
  }
}

function fail(
  code: string,
  message: string,
  context: Readonly<Record<string, unknown>> = {},
): never {
  throw new KValidationError(code, message, context)
}

export function normalizeEdge(edge: KEdge): KEdge {
  return edge.a < edge.b ? { a: edge.a, b: edge.b } : { a: edge.b, b: edge.a }
}

export function edgeKey(edge: KEdge): string {
  const normalized = normalizeEdge(edge)
  return `${normalized.a}\u0000${normalized.b}`
}

function compareEdges(left: KEdge, right: KEdge): number {
  return edgeKey(left).localeCompare(edgeKey(right))
}

export function normalizeKComplex(complex: KComplex): KComplex {
  return {
    vertices: [...complex.vertices].sort((left, right) => left.id.localeCompare(right.id)),
    edges: complex.edges.map(normalizeEdge).sort(compareEdges),
  }
}

function vertexMap(complex: KComplex): Map<KVertexId, KVertex> {
  return new Map(complex.vertices.map((vertex) => [vertex.id, vertex]))
}

function adjacency(complex: KComplex): Map<KVertexId, KVertexId[]> {
  const result = new Map<KVertexId, KVertexId[]>()
  for (const vertex of complex.vertices) result.set(vertex.id, [])
  for (const edge of complex.edges) {
    result.get(edge.a)?.push(edge.b)
    result.get(edge.b)?.push(edge.a)
  }
  return result
}

export function initialVertex(
  complex: KComplex,
  signature: KSignature,
): KVertex {
  const candidates = complex.vertices.filter(
    (vertex) =>
      vertex.type === signature.workingInitialType
      || vertex.type === signature.finalInitialType,
  )
  if (candidates.length !== 1) {
    return fail(
      'initial-vertex-count',
      `A K-complex must contain exactly one ${signature.workingInitialType}/${signature.finalInitialType} vertex; found ${candidates.length}`,
      { count: candidates.length },
    )
  }
  return candidates[0]!
}

function validateSignature(signature: KSignature): void {
  if (signature.types.length === 0) {
    fail('empty-signature', 'A K-machine signature must declare at least one type')
  }
  if (new Set(signature.types).size !== signature.types.length) {
    fail('duplicate-type', 'K-machine types must be unique')
  }
  if (signature.workingInitialType === signature.finalInitialType) {
    fail('initial-types-equal', 'Working and final initial types must be distinct')
  }
  if (!signature.types.includes(signature.workingInitialType)) {
    fail('missing-working-type', 'The working initial type must be declared')
  }
  if (!signature.types.includes(signature.finalInitialType)) {
    fail('missing-final-type', 'The final initial type must be declared')
  }
}

export function validateKComplex(
  complex: KComplex,
  signature: KSignature,
): void {
  validateSignature(signature)
  if (complex.vertices.length === 0) {
    fail('empty-complex', 'A K-complex must contain at least one vertex')
  }

  const byId = new Map<KVertexId, KVertex>()
  for (const vertex of complex.vertices) {
    if (!vertex.id) fail('empty-vertex-id', 'Vertex IDs must be non-empty')
    if (byId.has(vertex.id)) {
      fail('duplicate-vertex-id', `Duplicate vertex ID '${vertex.id}'`, { vertexId: vertex.id })
    }
    if (!signature.types.includes(vertex.type)) {
      fail(
        'undeclared-type',
        `Vertex '${vertex.id}' uses undeclared type '${vertex.type}'`,
        { vertexId: vertex.id, type: vertex.type },
      )
    }
    byId.set(vertex.id, vertex)
  }

  initialVertex(complex, signature)

  const seenEdges = new Set<string>()
  const neighborTypes = new Map<KVertexId, Set<KTypeId>>()
  for (const vertex of complex.vertices) neighborTypes.set(vertex.id, new Set())

  for (const rawEdge of complex.edges) {
    if (!byId.has(rawEdge.a) || !byId.has(rawEdge.b)) {
      fail('missing-edge-endpoint', 'Every edge endpoint must reference an existing vertex', {
        edge: rawEdge,
      })
    }
    if (rawEdge.a === rawEdge.b) {
      fail('self-loop', `Self-loop at '${rawEdge.a}' is not permitted`, { edge: rawEdge })
    }
    const key = edgeKey(rawEdge)
    if (seenEdges.has(key)) {
      fail('duplicate-edge', `Duplicate edge '${rawEdge.a}-${rawEdge.b}'`, { edge: rawEdge })
    }
    seenEdges.add(key)

    const aNeighborType = byId.get(rawEdge.b)?.type
    const bNeighborType = byId.get(rawEdge.a)?.type
    if (!aNeighborType || !bNeighborType) continue

    const atA = neighborTypes.get(rawEdge.a)
    const atB = neighborTypes.get(rawEdge.b)
    if (atA?.has(aNeighborType)) {
      fail(
        'duplicate-neighbor-type',
        `Vertex '${rawEdge.a}' has more than one neighbor of type '${aNeighborType}'`,
        { vertexId: rawEdge.a, type: aNeighborType },
      )
    }
    if (atB?.has(bNeighborType)) {
      fail(
        'duplicate-neighbor-type',
        `Vertex '${rawEdge.b}' has more than one neighbor of type '${bNeighborType}'`,
        { vertexId: rawEdge.b, type: bNeighborType },
      )
    }
    atA?.add(aNeighborType)
    atB?.add(bNeighborType)
  }
}

export function partitionKComplex(
  complex: KComplex,
  signature: KSignature,
  radius: number,
): KPartition {
  if (!Number.isInteger(radius) || radius < 1) {
    fail('invalid-radius', 'The 1958 profile requires a positive integer radius', { radius })
  }
  validateKComplex(complex, signature)

  const root = initialVertex(complex, signature)
  const graph = adjacency(complex)
  const distances = new Map<KVertexId, number>([[root.id, 0]])
  const queue = [root.id]

  for (let cursor = 0; cursor < queue.length; cursor++) {
    const current = queue[cursor]!
    const distance = distances.get(current)
    if (distance === undefined) continue
    for (const neighbor of graph.get(current) ?? []) {
      if (!distances.has(neighbor)) {
        distances.set(neighbor, distance + 1)
        queue.push(neighbor)
      }
    }
  }

  const activeVertices = complex.vertices
    .filter((vertex) => (distances.get(vertex.id) ?? Number.POSITIVE_INFINITY) <= radius)
    .map((vertex) => vertex.id)
    .sort()
  const frontierVertices = complex.vertices
    .filter((vertex) => distances.get(vertex.id) === radius)
    .map((vertex) => vertex.id)
    .sort()
  const externalVertices = complex.vertices
    .filter((vertex) => {
      const distance = distances.get(vertex.id)
      return distance === undefined || distance >= radius
    })
    .map((vertex) => vertex.id)
    .sort()

  const activeEdges: KEdge[] = []
  const externalEdges: KEdge[] = []
  for (const rawEdge of complex.edges) {
    const edge = normalizeEdge(rawEdge)
    const distanceA = distances.get(edge.a) ?? Number.POSITIVE_INFINITY
    const distanceB = distances.get(edge.b) ?? Number.POSITIVE_INFINITY
    if (Math.min(distanceA, distanceB) + 1 <= radius) activeEdges.push(edge)
    else externalEdges.push(edge)
  }

  const distance: Record<KVertexId, number | null> = {}
  for (const vertex of complex.vertices) distance[vertex.id] = distances.get(vertex.id) ?? null

  return {
    activeVertices,
    activeEdges: activeEdges.sort(compareEdges),
    frontierVertices,
    externalVertices,
    externalEdges: externalEdges.sort(compareEdges),
    distance,
  }
}

export function activeSubcomplex(complex: KComplex, partition: KPartition): KComplex {
  const active = new Set(partition.activeVertices)
  return normalizeKComplex({
    vertices: complex.vertices.filter((vertex) => active.has(vertex.id)),
    edges: partition.activeEdges,
  })
}

export function canonicalizeRootedComplex(
  complex: KComplex,
  signature: KSignature,
): KCanonicalComplex {
  validateKComplex(complex, signature)
  const root = initialVertex(complex, signature)
  const byId = vertexMap(complex)
  const graph = adjacency(complex)
  const typeOrder = new Map(signature.types.map((type, index) => [type, index]))
  const orderedVertexIds: KVertexId[] = []
  const visited = new Set<KVertexId>([root.id])
  const queue = [root.id]

  for (let cursor = 0; cursor < queue.length; cursor++) {
    const current = queue[cursor]!
    orderedVertexIds.push(current)
    const neighbors = [...(graph.get(current) ?? [])].sort((left, right) => {
      const leftType = byId.get(left)?.type
      const rightType = byId.get(right)?.type
      return (typeOrder.get(leftType ?? '') ?? Number.MAX_SAFE_INTEGER)
        - (typeOrder.get(rightType ?? '') ?? Number.MAX_SAFE_INTEGER)
    })
    for (const neighbor of neighbors) {
      if (!visited.has(neighbor)) {
        visited.add(neighbor)
        queue.push(neighbor)
      }
    }
  }

  if (orderedVertexIds.length !== complex.vertices.length) {
    fail(
      'canonical-disconnected',
      'A rooted complex must be connected before it can be canonically matched',
      { reached: orderedVertexIds.length, total: complex.vertices.length },
    )
  }

  const canonicalIndex = new Map(orderedVertexIds.map((id, index) => [id, index]))
  const types = orderedVertexIds.map((id) => byId.get(id)?.type)
  const edges = complex.edges
    .map((edge) => {
      const a = canonicalIndex.get(edge.a)
      const b = canonicalIndex.get(edge.b)
      if (a === undefined || b === undefined) {
        return fail('canonical-edge', 'Canonical edge endpoint was not indexed', { edge })
      }
      return (a < b ? [a, b] : [b, a]) as readonly [number, number]
    })
    .sort((left, right) => left[0] - right[0] || left[1] - right[1])

  return {
    orderedVertexIds,
    fingerprint: JSON.stringify({ types, edges }),
  }
}

function exactComplexFingerprint(complex: KComplex): string {
  const normalized = normalizeKComplex(complex)
  return JSON.stringify({
    vertices: normalized.vertices.map((vertex) => [vertex.id, vertex.type]),
    edges: normalized.edges.map((edge) => [edge.a, edge.b]),
  })
}

function neighborTypeSet(
  complex: KComplex,
  vertexId: KVertexId,
): Set<KTypeId> {
  const byId = vertexMap(complex)
  const result = new Set<KTypeId>()
  for (const edge of complex.edges) {
    if (edge.a === vertexId) {
      const type = byId.get(edge.b)?.type
      if (type) result.add(type)
    } else if (edge.b === vertexId) {
      const type = byId.get(edge.a)?.type
      if (type) result.add(type)
    }
  }
  return result
}

export function validateKRule(
  rule: KRule,
  signature: KSignature,
  radius: number,
): void {
  if (!rule.id) fail('empty-rule-id', 'Rule IDs must be non-empty')
  validateKComplex(rule.input, signature)
  validateKComplex(rule.output, signature)

  if (initialVertex(rule.input, signature).type !== signature.workingInitialType) {
    fail('final-rule-input', `Rule '${rule.id}' must have a working T0 input`)
  }

  const partition = partitionKComplex(rule.input, signature, radius)
  if (
    partition.activeVertices.length !== rule.input.vertices.length
    || partition.activeEdges.length !== rule.input.edges.length
  ) {
    fail(
      'incomplete-rule-input',
      `Rule '${rule.id}' input must equal its complete radius-${radius} active part`,
      { ruleId: rule.id },
    )
  }

  const inputById = vertexMap(rule.input)
  const outputById = vertexMap(rule.output)
  const frontier = new Set(partition.frontierVertices)
  const mappedInputs = new Set<KVertexId>()
  const mappedOutputs = new Set<KVertexId>()

  if (rule.attachments.length !== frontier.size) {
    fail(
      'attachment-count',
      `Rule '${rule.id}' must map every frontier vertex exactly once`,
      { expected: frontier.size, actual: rule.attachments.length },
    )
  }

  for (const attachment of rule.attachments) {
    if (!frontier.has(attachment.inputBoundary)) {
      fail(
        'attachment-not-frontier',
        `Rule '${rule.id}' maps non-frontier input '${attachment.inputBoundary}'`,
      )
    }
    if (mappedInputs.has(attachment.inputBoundary)) {
      fail('duplicate-input-attachment', `Rule '${rule.id}' maps an input frontier twice`)
    }
    if (!outputById.has(attachment.outputVertex)) {
      fail(
        'missing-output-attachment',
        `Rule '${rule.id}' maps to missing output vertex '${attachment.outputVertex}'`,
      )
    }
    if (mappedOutputs.has(attachment.outputVertex)) {
      fail('duplicate-output-attachment', `Rule '${rule.id}' reuses an output attachment`)
    }
    const inputType = inputById.get(attachment.inputBoundary)?.type
    const outputType = outputById.get(attachment.outputVertex)?.type
    if (inputType !== outputType) {
      fail(
        'attachment-type-mismatch',
        `Rule '${rule.id}' attachment types must agree`,
        { inputType, outputType },
      )
    }
    mappedInputs.add(attachment.inputBoundary)
    mappedOutputs.add(attachment.outputVertex)
  }

  for (const edge of rule.output.edges) {
    if (mappedOutputs.has(edge.a) && mappedOutputs.has(edge.b)) {
      fail(
        'nonzero-attachment-subcomplex',
        `Rule '${rule.id}' output attachment set must be zero-dimensional`,
        { edge },
      )
    }
  }

  for (const attachment of rule.attachments) {
    const allowed = neighborTypeSet(rule.input, attachment.inputBoundary)
    const outputNeighbors = neighborTypeSet(rule.output, attachment.outputVertex)
    for (const type of outputNeighbors) {
      const isInitial = type === signature.workingInitialType || type === signature.finalInitialType
      if (!isInitial && !allowed.has(type)) {
        fail(
          'attachment-neighbor-type',
          `Rule '${rule.id}' introduces non-initial neighbor type '${type}' at an attachment`,
          { attachment, type },
        )
      }
    }
  }
}

export function compileKMachine(machine: KMachine): CompiledKMachine {
  if (machine.model !== 'ku-1958' || machine.schemaVersion !== 1) {
    fail('machine-profile', 'Unsupported K-machine profile or schema version')
  }
  if (!Number.isInteger(machine.radius) || machine.radius < 1) {
    fail('invalid-radius', 'A K-machine radius must be a positive integer')
  }
  validateSignature(machine.signature)

  const rulesByFingerprint: Record<string, CompiledKRule> = {}
  const ids = new Set<string>()
  for (const rule of machine.rules) {
    if (ids.has(rule.id)) fail('duplicate-rule-id', `Duplicate rule ID '${rule.id}'`)
    ids.add(rule.id)
    validateKRule(rule, machine.signature, machine.radius)
    const inputCanonical = canonicalizeRootedComplex(rule.input, machine.signature)
    const existing = rulesByFingerprint[inputCanonical.fingerprint]
    if (existing) {
      fail(
        'isomorphic-rule-inputs',
        `Rule '${rule.id}' has an input isomorphic to '${existing.source.id}'`,
      )
    }
    rulesByFingerprint[inputCanonical.fingerprint] = { source: rule, inputCanonical }
  }

  return { source: machine, rulesByFingerprint }
}

export function createKExecution(
  complex: KComplex,
  machine: CompiledKMachine,
): KExecutionState {
  validateKComplex(complex, machine.source.signature)
  const normalized = normalizeKComplex(complex)
  const initial = initialVertex(normalized, machine.source.signature)
  return {
    complex: normalized,
    step: 0,
    milestones: 0,
    nextVertexSerial: 0,
    outcome:
      initial.type === machine.source.signature.finalInitialType ? 'result' : 'running',
    lastTrace: null,
  }
}

function nextFreshId(
  occupied: Set<KVertexId>,
  serial: number,
): { id: KVertexId; nextSerial: number } {
  let cursor = serial
  while (occupied.has(`k${cursor}`)) cursor++
  const id = `k${cursor}`
  occupied.add(id)
  return { id, nextSerial: cursor + 1 }
}

function mapRecord(entries: Iterable<readonly [string, string]>): Readonly<Record<string, string>> {
  return Object.fromEntries(entries)
}

function noResultTrace(
  state: KExecutionState,
  partition: KPartition,
  activeFingerprint: string,
  machine: CompiledKMachine,
): KStepTrace {
  const fingerprint = exactComplexFingerprint(state.complex)
  return {
    machineId: machine.source.id,
    profile: machine.source.model,
    schemaVersion: machine.source.schemaVersion,
    step: state.step,
    partition,
    activeFingerprint,
    matchedRuleId: null,
    patternToRuntime: {},
    attachmentMapping: {},
    preservedVertices: state.complex.vertices.map((vertex) => vertex.id),
    removedVertices: [],
    createdVertices: [],
    preservedEdges: normalizeKComplex(state.complex).edges,
    removedEdges: [],
    createdEdges: [],
    beforeFingerprint: fingerprint,
    afterFingerprint: fingerprint,
    outcome: 'no-result',
    events: [{ type: 'outcome', outcome: 'no-result' }],
  }
}

export function stepK(
  state: KExecutionState,
  machine: CompiledKMachine,
): KExecutionState {
  if (state.outcome !== 'running') return state

  validateKComplex(state.complex, machine.source.signature)
  const partition = partitionKComplex(
    state.complex,
    machine.source.signature,
    machine.source.radius,
  )
  const active = activeSubcomplex(state.complex, partition)
  const runtimeCanonical = canonicalizeRootedComplex(active, machine.source.signature)
  const compiledRule = machine.rulesByFingerprint[runtimeCanonical.fingerprint]

  if (!compiledRule) {
    return {
      ...state,
      outcome: 'no-result',
      lastTrace: noResultTrace(state, partition, runtimeCanonical.fingerprint, machine),
    }
  }

  const patternToRuntime = new Map<KVertexId, KVertexId>()
  for (let index = 0; index < compiledRule.inputCanonical.orderedVertexIds.length; index++) {
    const patternId = compiledRule.inputCanonical.orderedVertexIds[index]
    const runtimeId = runtimeCanonical.orderedVertexIds[index]
    if (!patternId || !runtimeId) {
      fail('canonical-correspondence', 'Canonical rule/runtime correspondence is incomplete')
    }
    patternToRuntime.set(patternId, runtimeId)
  }

  const outputToRuntime = new Map<KVertexId, KVertexId>()
  const attachmentMapping = new Map<KVertexId, KVertexId>()
  for (const attachment of compiledRule.source.attachments) {
    const runtimeBoundary = patternToRuntime.get(attachment.inputBoundary)
    if (!runtimeBoundary) {
      fail('runtime-attachment', 'Unable to locate a runtime frontier attachment', {
        attachment,
      })
    }
    outputToRuntime.set(attachment.outputVertex, runtimeBoundary)
    attachmentMapping.set(attachment.inputBoundary, runtimeBoundary)
  }

  const externalIds = new Set(partition.externalVertices)
  const occupied = new Set(state.complex.vertices.map((vertex) => vertex.id))
  const createdVertices: KVertexId[] = []
  const nextVertices = state.complex.vertices.filter((vertex) => externalIds.has(vertex.id))
  let nextSerial = state.nextVertexSerial

  for (const outputVertex of compiledRule.source.output.vertices) {
    if (outputToRuntime.has(outputVertex.id)) continue
    const fresh = nextFreshId(occupied, nextSerial)
    nextSerial = fresh.nextSerial
    outputToRuntime.set(outputVertex.id, fresh.id)
    createdVertices.push(fresh.id)
    nextVertices.push({ id: fresh.id, type: outputVertex.type })
  }

  const createdEdges = compiledRule.source.output.edges.map((edge) => {
    const a = outputToRuntime.get(edge.a)
    const b = outputToRuntime.get(edge.b)
    if (!a || !b) fail('output-instantiation', 'Unable to instantiate a rule output edge', { edge })
    return normalizeEdge({ a, b })
  })
  const nextComplex = normalizeKComplex({
    vertices: nextVertices,
    edges: [...partition.externalEdges, ...createdEdges],
  })
  validateKComplex(nextComplex, machine.source.signature)

  const nextInitial = initialVertex(nextComplex, machine.source.signature)
  const outcome =
    nextInitial.type === machine.source.signature.finalInitialType ? 'result' : 'running'
  const removedVertices = partition.activeVertices.filter(
    (vertexId) => !partition.frontierVertices.includes(vertexId),
  )
  const events: KStepTrace['events'] = [
    {
      type: 'rule-applied',
      ruleId: compiledRule.source.id,
      title: compiledRule.source.title,
      tags: compiledRule.source.tags ?? [],
    },
    ...(outcome === 'result'
      ? ([{ type: 'outcome', outcome: 'result' }] as const)
      : []),
  ]
  const trace: KStepTrace = {
    machineId: machine.source.id,
    profile: machine.source.model,
    schemaVersion: machine.source.schemaVersion,
    step: state.step + 1,
    partition,
    activeFingerprint: runtimeCanonical.fingerprint,
    matchedRuleId: compiledRule.source.id,
    patternToRuntime: mapRecord(patternToRuntime.entries()),
    attachmentMapping: mapRecord(attachmentMapping.entries()),
    preservedVertices: partition.externalVertices,
    removedVertices,
    createdVertices: [...createdVertices].sort(),
    preservedEdges: partition.externalEdges,
    removedEdges: partition.activeEdges,
    createdEdges: createdEdges.sort(compareEdges),
    beforeFingerprint: exactComplexFingerprint(state.complex),
    afterFingerprint: exactComplexFingerprint(nextComplex),
    outcome,
    events,
  }

  return {
    complex: nextComplex,
    step: state.step + 1,
    milestones: state.milestones + ((compiledRule.source.tags ?? []).includes('traversal') ? 1 : 0),
    nextVertexSerial: nextSerial,
    outcome,
    lastTrace: trace,
  }
}

export function runK(
  initialState: KExecutionState,
  machine: CompiledKMachine,
  maxSteps: number,
): KRunResult {
  if (!Number.isInteger(maxSteps) || maxSteps < 0) {
    fail('invalid-step-budget', 'Step budget must be a non-negative integer', { maxSteps })
  }
  let state = initialState
  let transitions = 0
  while (state.outcome === 'running' && transitions < maxSteps) {
    state = stepK(state, machine)
    transitions++
  }
  return {
    state,
    status: state.outcome === 'running' ? 'budget-exhausted' : state.outcome,
  }
}

export function extractKResultComponent(
  state: KExecutionState,
  signature: KSignature,
): KComplex | null {
  if (state.outcome !== 'result') return null
  const root = initialVertex(state.complex, signature)
  const graph = adjacency(state.complex)
  const reached = new Set<KVertexId>([root.id])
  const queue = [root.id]
  for (let cursor = 0; cursor < queue.length; cursor++) {
    const current = queue[cursor]!
    for (const neighbor of graph.get(current) ?? []) {
      if (!reached.has(neighbor)) {
        reached.add(neighbor)
        queue.push(neighbor)
      }
    }
  }
  return normalizeKComplex({
    vertices: state.complex.vertices.filter((vertex) => reached.has(vertex.id)),
    edges: state.complex.edges.filter((edge) => reached.has(edge.a) && reached.has(edge.b)),
  })
}
